import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Camera, Clock, Package, StickyNote, Play, Square,
  Upload, Plus, Loader2, ChevronRight, MapPin, Image,
  Trash2, PoundSterling, FileText, Users, User, Pencil,
  WifiOff, Wifi, RefreshCw, CloudOff
} from "lucide-react";
import { toast } from "sonner";
import { getStatusInfo, formatDate } from "@/lib/constants";
import { MetricInfo } from "@/components/admin/MetricInfo";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { JobMaterialsPanel } from "@/components/admin/JobMaterialsPanel";
import { JobTimePanel } from "@/components/admin/JobTimePanel";
import { WhatsAppButton } from "@/components/admin/WhatsAppButton";

const API = `${window.location.origin}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SiteModeTab({ authHeaders }) {
  const [activeJobs, setActiveJobs] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [materialsCount, setMaterialsCount] = useState(0);
  // Iter 66: When the user taps the Materials quick-action we bump this key
  // so <JobMaterialsPanel/> refetches the DB instead of showing stale cached
  // rows from the previous open.
  const [materialsRefreshKey, setMaterialsRefreshKey] = useState(0);
  
  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStart, setTimerStart] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  
  // Form states
  const [materialName, setMaterialName] = useState("");
  const [materialQty, setMaterialQty] = useState("");
  const [materialCost, setMaterialCost] = useState("");
  const [note, setNote] = useState("");
  const [photoCaption, setPhotoCaption] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  
  // Team size
  const [teamSize, setTeamSize] = useState(1);
  
  // Invoice generation
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceLineItems, setInvoiceLineItems] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [invoiceCustomerName, setInvoiceCustomerName] = useState("");
  const [invoiceCustomerAddress, setInvoiceCustomerAddress] = useState("");
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mountedRef = useRef(true);
  const fetchedRef = useRef(false);

  // Offline queue
  const { isOnline, queue, queueCount, isSyncing, enqueue, processQueue } = useOfflineQueue(API, getAuthHeaders);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      // Phase 2: auth is an httpOnly cookie — NEVER gate on localStorage.
      // The old localStorage check skipped this fetch entirely for cookie
      // sessions, so Site Mode showed "No active jobs" forever.
      fetch(`${API}/site-mode/active-jobs`, { headers: getAuthHeaders(), credentials: "same-origin" })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (mountedRef.current) {
            setActiveJobs(data.jobs || []);
            setDiagnostics(data.diagnostics || null);
            setLoading(false);
          }
        })
        .catch(err => {
          console.error("[SiteMode] Fetch error:", err.message);
          if (mountedRef.current) setLoading(false);
        });
    }
    
    return () => { mountedRef.current = false; fetchedRef.current = false; };
  }, []);

  // Timer tick
  useEffect(() => {
    let interval;
    if (timerRunning && timerStart) {
      interval = setInterval(() => {
        setElapsed(Math.floor((Date.now() - timerStart) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timerStart]);

  const selectJob = async (job) => {
    setSelectedJob(job);
    setTeamSize(job.team_size || 1);
    await fetchJobLogs(job.id);
    await checkActiveTimer(job.id);
    fetchMaterialsCount(job.id);
  };

  const fetchMaterialsCount = async (jobId) => {
    try {
      const res = await fetch(`${API}/admin/materials?job_id=${encodeURIComponent(jobId)}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) return;
      const data = await res.json();
      if (mountedRef.current) {
        setMaterialsCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchJobLogs = async (jobId) => {
    try {
      const res = await fetch(`${API}/site-mode/job/${jobId}/logs`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (mountedRef.current) {
        setLogs(data.logs || []);
        setSummary(data.summary || null);
      }
    } catch (err) {
      console.error("[SiteMode] Failed to fetch logs:", err.message);
    }
  };

  const checkActiveTimer = async (jobId) => {
    try {
      const res = await fetch(`${API}/site-mode/timer/${jobId}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.timer) {
        setTimerRunning(true);
        setTimerStart(new Date(data.timer.started_at).getTime());
        setElapsed(Math.floor((Date.now() - new Date(data.timer.started_at).getTime()) / 1000));
      } else {
        setTimerRunning(false);
        setTimerStart(null);
        setElapsed(0);
      }
    } catch (err) {
      console.error("[SiteMode] Timer check failed:", err.message);
    }
  };

  const toggleTimer = async () => {
    if (!selectedJob) return;
    try {
      const action = timerRunning ? 'stop' : 'start';
      const res = await fetch(`${API}/site-mode/timer`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enquiry_id: selectedJob.id, action })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      if (action === 'start') {
        setTimerRunning(true);
        setTimerStart(Date.now());
        setElapsed(0);
        toast.success("Timer started");
      } else {
        setTimerRunning(false);
        setTimerStart(null);
        toast.success(`Timer stopped: ${data.duration_minutes} minutes logged`);
        await fetchJobLogs(selectedJob.id);
      }
    } catch (err) {
      toast.error("Timer error");
    }
  };

  const addMaterial = async () => {
    if (!materialName.trim() || !selectedJob) return;
    const payload = {
      enquiry_id: selectedJob.id,
      log_type: "materials",
      content: materialName.trim(),
      quantity: materialQty ? parseFloat(materialQty) : null,
      unit: "units",
      cost: materialCost ? parseFloat(materialCost) : null
    };

    if (!isOnline) {
      enqueue({ endpoint: "site-mode/log", method: "POST", body: payload, label: `Material: ${materialName.trim()}` });
      toast.info("Saved offline — will sync when back online");
      setMaterialName(""); setMaterialQty(""); setMaterialCost("");
      setActiveSection(null);
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API}/site-mode/log`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Material logged");
      setMaterialName(""); setMaterialQty(""); setMaterialCost("");
      setActiveSection(null);
      await fetchJobLogs(selectedJob.id);
    } catch (err) {
      enqueue({ endpoint: "site-mode/log", method: "POST", body: payload, label: `Material: ${materialName.trim()}` });
      toast.info("Network error — queued for sync");
      setMaterialName(""); setMaterialQty(""); setMaterialCost("");
      setActiveSection(null);
    } finally {
      setIsSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim() || !selectedJob) return;
    const payload = {
      enquiry_id: selectedJob.id,
      log_type: "note",
      content: note.trim()
    };

    if (!isOnline) {
      enqueue({ endpoint: "site-mode/log", method: "POST", body: payload, label: `Note: ${note.trim().substring(0, 40)}` });
      toast.info("Saved offline — will sync when back online");
      setNote(""); setActiveSection(null);
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API}/site-mode/log`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Note saved");
      setNote(""); setActiveSection(null);
      await fetchJobLogs(selectedJob.id);
    } catch (err) {
      enqueue({ endpoint: "site-mode/log", method: "POST", body: payload, label: `Note: ${note.trim().substring(0, 40)}` });
      toast.info("Network error — queued for sync");
      setNote(""); setActiveSection(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoFiles = async (files) => {
    for (const f of Array.from(files || [])) {
      await handlePhotoUpload(f);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !selectedJob) return;
    if (!isOnline) {
      toast.info("Photos require an internet connection — please try again when online");
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('enquiry_id', selectedJob.id);
      formData.append('caption', photoCaption || file.name);
      
      const res = await fetch(`${API}/site-mode/photo`, {
        method: 'POST',
        headers: { Authorization: getAuthHeaders().Authorization },
        body: formData
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Photo uploaded");
      setPhotoCaption("");
      await fetchJobLogs(selectedJob.id);
    } catch (err) {
      toast.error("Photo upload failed — try again when connection is stable");
    } finally {
      setIsUploading(false);
    }
  };

  const changeTeamSize = async (size) => {
    const newSize = parseInt(size);
    setTeamSize(newSize);
    if (!selectedJob) return;
    const payload = { enquiry_id: selectedJob.id, team_size: newSize };

    if (!isOnline) {
      enqueue({ endpoint: "site-mode/team-size", method: "POST", body: payload, label: `Team: ${newSize} man` });
      return;
    }

    try {
      await fetch(`${API}/site-mode/team-size`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      enqueue({ endpoint: "site-mode/team-size", method: "POST", body: payload, label: `Team: ${newSize} man` });
    }
  };

  const openInvoicePreview = async () => {
    if (!selectedJob) return;
    setIsGenerating(true);
    try {
      const res = await fetch(`${API}/site-mode/job/${selectedJob.id}/invoice-preview`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const preview = await res.json();
      setInvoicePreview(preview);
      setInvoiceCustomerName(preview.customer_name || "");
      setInvoiceCustomerAddress(preview.customer_address || "");
      setInvoiceLineItems(preview.line_items.map((item, idx) => ({ ...item, id: idx })));
      setShowInvoiceDialog(true);
    } catch (err) {
      toast.error("Failed to load invoice preview");
    } finally {
      setIsGenerating(false);
    }
  };

  const updateLineItem = (idx, field, value) => {
    setInvoiceLineItems(prev => prev.map((item, i) => 
      i === idx ? { ...item, [field]: field === 'description' ? value : parseFloat(value) || 0 } : item
    ));
  };

  const removeLineItem = (idx) => {
    setInvoiceLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const addLineItem = () => {
    setInvoiceLineItems(prev => [...prev, { description: "", quantity: 1, unit_cost: 0, total: 0, id: Date.now() }]);
  };

  const generateInvoice = async () => {
    if (!selectedJob || invoiceLineItems.length === 0) return;
    setIsGenerating(true);
    try {
      const res = await fetch(`${API}/admin/invoices`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: invoiceCustomerName,
          customer_address: invoiceCustomerAddress,
          description: selectedJob.message || "",
          line_items: invoiceLineItems.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit_cost: item.unit_cost
          })),
          payment_days: 7,
          job_id: selectedJob.id
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Invoice created successfully!");
      setShowInvoiceDialog(false);
    } catch (err) {
      toast.error("Failed to create invoice");
    } finally {
      setIsGenerating(false);
    }
  };

  const formatElapsed = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${window.location.origin}${url}`;
    if (url.includes('drive.google.com/uc?id=')) {
      const id = url.split('id=')[1];
      return `https://lh3.googleusercontent.com/d/${id}=s400`;
    }
    return url;
  };

  // Refresh logs after sync completes
  const handleManualSync = async () => {
    const synced = await processQueue();
    if (synced > 0 && selectedJob) {
      await fetchJobLogs(selectedJob.id);
    }
  };

  // Offline status bar
  const OfflineBar = () => {
    if (isOnline && queueCount === 0) return null;
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
          !isOnline
            ? "bg-amber-50 border border-amber-200 text-amber-800"
            : "bg-blue-50 border border-blue-200 text-blue-800"
        }`}
        data-testid="offline-status-bar"
      >
        {!isOnline ? (
          <>
            <WifiOff className="h-4 w-4 shrink-0" />
            <span className="flex-1">Offline — entries will sync when reconnected</span>
            {queueCount > 0 && (
              <Badge variant="secondary" className="bg-amber-200 text-amber-900" data-testid="offline-queue-count">
                {queueCount} queued
              </Badge>
            )}
          </>
        ) : (
          <>
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 animate-spin shrink-0" />
            ) : (
              <CloudOff className="h-4 w-4 shrink-0" />
            )}
            <span className="flex-1">
              {isSyncing ? "Syncing..." : `${queueCount} item${queueCount > 1 ? "s" : ""} pending sync`}
            </span>
            {!isSyncing && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 border-blue-300"
                onClick={handleManualSync}
                data-testid="sync-now-btn"
              >
                <RefreshCw className="h-3 w-3" /> Sync now
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  // Job list view
  if (!selectedJob) {
    return (
      <div className="space-y-4">
        <OfflineBar />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Site Mode
              <MetricInfo metricKey="site_mode_active_jobs" />
            </h1>
            <p className="text-sm text-muted-foreground">Quick log from the field</p>
          </div>
          {!loading && (
            <Badge variant="secondary" data-testid="site-active-jobs-count">
              {activeJobs.length} active job{activeJobs.length === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : activeJobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center" data-testid="site-mode-empty-state">
              <MapPin className="h-12 w-12 mx-auto mb-3 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground font-medium">No eligible active jobs</p>
              <p className="text-xs text-muted-foreground mt-1">
                Only jobs at Accepted, Scheduled or In Progress stage qualify. Every team member sees the same list — a past or missing date never hides a job.
              </p>
              {diagnostics && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="site-mode-diagnostics">
                  Checked {diagnostics.scanned} job{diagnostics.scanned === 1 ? "" : "s"} — none is currently at an eligible stage{diagnostics.excluded_flagged > 0 ? ` (${diagnostics.excluded_flagged} archived/deleted excluded)` : ""}.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {activeJobs.map(job => {
              const statusInfo = getStatusInfo(job.status, 'job');
              return (
                <Card 
                  key={job.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]"
                  onClick={() => selectJob(job)}
                  data-testid={`site-job-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{job.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{job.message?.substring(0, 60) || job.service}</p>
                        {job.preferred_date && (
                          <p className="text-xs text-muted-foreground mt-1">{formatDate(job.preferred_date)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <Badge className={statusInfo.bgColor}>{statusInfo.label}</Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Job detail / logging view
  const statusInfo = getStatusInfo(selectedJob.status, 'job');

  return (
    <div className="space-y-4">
      <OfflineBar />
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedJob(null); setActiveSection(null); }} data-testid="site-back-btn">
          <ChevronRight className="h-4 w-4 rotate-180" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold truncate">{selectedJob.name}</h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <Badge className={statusInfo.bgColor} data-testid="site-job-status">{statusInfo.label}</Badge>
            {selectedJob.preferred_date && (
              <span className="text-xs text-muted-foreground">{formatDate(selectedJob.preferred_date)}</span>
            )}
            {selectedJob.phone && (
              <div className="flex items-center gap-1.5">
                <a
                  href={`tel:${selectedJob.phone}`}
                  className="text-xs text-primary hover:underline"
                  data-testid="site-job-phone"
                >
                  {selectedJob.phone}
                </a>
                <WhatsAppButton
                  phone={selectedJob.phone}
                  name={selectedJob.name}
                  service={selectedJob.service}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timer Card + Team Size */}
      <Card className={timerRunning ? "border-green-300 bg-green-50" : ""}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Time Tracker</p>
              <p className={`text-3xl font-mono font-bold ${timerRunning ? 'text-green-700' : ''}`} data-testid="site-timer-display">
                {formatElapsed(elapsed)}
              </p>
            </div>
            <Button 
              size="lg" 
              onClick={toggleTimer}
              className={timerRunning ? "bg-red-500 hover:bg-red-600" : "bg-green-600 hover:bg-green-700"}
              data-testid="site-timer-btn"
            >
              {timerRunning ? <Square className="h-5 w-5 mr-2" /> : <Play className="h-5 w-5 mr-2" />}
              {timerRunning ? 'Stop' : 'Start'}
            </Button>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <span className="text-sm text-muted-foreground">Team:</span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={teamSize === 1 ? "default" : "outline"}
                className={`h-8 gap-1.5 ${teamSize === 1 ? 'bg-primary' : ''}`}
                onClick={() => changeTeamSize(1)}
                data-testid="team-size-1"
              >
                <User className="h-3.5 w-3.5" />
                1 Man
              </Button>
              <Button
                size="sm"
                variant={teamSize === 2 ? "default" : "outline"}
                className={`h-8 gap-1.5 ${teamSize === 2 ? 'bg-primary' : ''}`}
                onClick={() => changeTeamSize(2)}
                data-testid="team-size-2"
              >
                <Users className="h-3.5 w-3.5" />
                2 Man
              </Button>
            </div>
            <span className="text-xs text-muted-foreground ml-auto">
              £{teamSize === 1 ? '37.50' : '57.50'}/hr
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Manual time entries (separate from live timer — never affects billing) */}
      <JobTimePanel jobId={selectedJob.id} getAuthHeaders={getAuthHeaders} />

      {/* Quick Action Buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant={activeSection === 'photo' ? 'default' : 'outline'}
          className={`h-20 flex-col gap-1 ${!isOnline ? 'opacity-50' : ''}`}
          onClick={() => {
            if (!isOnline) { toast.info("Photos require an internet connection"); return; }
            setActiveSection(activeSection === 'photo' ? null : 'photo');
          }}
          data-testid="site-photo-btn"
        >
          <Camera className="h-6 w-6" />
          <span className="text-xs">{isOnline ? 'Photo' : 'Photo (offline)'}</span>
        </Button>
        <Button
          variant={activeSection === 'materials' ? 'default' : 'outline'}
          className="h-20 flex-col gap-1 relative"
          onClick={() => {
            const next = activeSection === 'materials' ? null : 'materials';
            setActiveSection(next);
            if (selectedJob) fetchMaterialsCount(selectedJob.id);
            // Iter 66: force the embedded panel to re-fetch whenever the
            // section is reopened so field crew always see current stock.
            if (next === 'materials') setMaterialsRefreshKey(k => k + 1);
          }}
          data-testid="site-materials-btn"
        >
          <Package className="h-6 w-6" />
          <span className="text-xs">Materials</span>
          {materialsCount > 0 && (
            <span
              className="absolute top-1 right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
              data-testid="site-materials-badge"
            >
              {materialsCount}
            </span>
          )}
        </Button>
        <Button
          variant={activeSection === 'note' ? 'default' : 'outline'}
          className="h-20 flex-col gap-1"
          onClick={() => setActiveSection(activeSection === 'note' ? null : 'note')}
          data-testid="site-note-btn"
        >
          <StickyNote className="h-6 w-6" />
          <span className="text-xs">Note</span>
        </Button>
      </div>

      {/* Photo Section */}
      {activeSection === 'photo' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="font-medium text-sm">Take or Upload Photo</p>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                className="h-16 flex-col gap-1"
                onClick={() => cameraInputRef.current?.click()}
                disabled={isUploading}
              >
                <Camera className="h-5 w-5" />
                <span className="text-xs">Camera</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-16 flex-col gap-1"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload className="h-5 w-5" />
                <span className="text-xs">Gallery</span>
              </Button>
            </div>
            <Input 
              placeholder="Photo caption (optional)" 
              value={photoCaption} 
              onChange={e => setPhotoCaption(e.target.value)}
              data-testid="site-photo-caption"
            />
            {isUploading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading to Google Drive...
              </div>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              capture="environment"
              className="hidden"
              onChange={e => e.target.files[0] && handlePhotoUpload(e.target.files[0])}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              onChange={e => { handlePhotoFiles(e.target.files); e.target.value = ""; }}
            />
          </CardContent>
        </Card>
      )}

      {/* Materials Section */}
      {activeSection === 'materials' && (
        <div className="space-y-3">
          {/* Live Materials Tracker (shared with admin dashboard) */}
          <Card>
            <CardContent className="p-4">
              <JobMaterialsPanel
                key={`${selectedJob.id}-${materialsRefreshKey}`}
                authHeaders={authHeaders}
                jobId={selectedJob.id}
                refreshKey={materialsRefreshKey}
              />
            </CardContent>
          </Card>

          {/* Legacy quick-log (stored in site-mode logs for time-stamped journal) */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="font-medium text-sm">Journal entry (optional)</p>
              <p className="text-xs text-muted-foreground">Adds a timestamped note to the site log — keep using the tracker above for ordering.</p>
              <Input 
                placeholder="Material name" 
                value={materialName} 
                onChange={e => setMaterialName(e.target.value)}
                data-testid="site-material-name"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input 
                  placeholder="Quantity" 
                  type="number" 
                  value={materialQty} 
                  onChange={e => setMaterialQty(e.target.value)}
                  data-testid="site-material-qty"
                />
                <div className="relative">
                  <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Cost" 
                    type="number" 
                    step="0.01"
                    className="pl-9"
                    value={materialCost} 
                    onChange={e => setMaterialCost(e.target.value)}
                    data-testid="site-material-cost"
                  />
                </div>
              </div>
              <Button 
                onClick={addMaterial} 
                disabled={!materialName.trim() || isSaving} 
                variant="outline"
                className="w-full"
                data-testid="site-material-save"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Add to journal
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Note Section */}
      {activeSection === 'note' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="font-medium text-sm">Add Note</p>
            <Textarea 
              placeholder="Type your note here..." 
              value={note} 
              onChange={e => setNote(e.target.value)}
              rows={3}
              data-testid="site-note-input"
            />
            <Button 
              onClick={addNote} 
              disabled={!note.trim() || isSaving}
              className="w-full"
              data-testid="site-note-save"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <StickyNote className="h-4 w-4 mr-2" />}
              Save Note
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-2">
          <Card>
            <CardContent className="p-3 text-center">
              <Clock className="h-4 w-4 mx-auto text-blue-600 mb-1" />
              <p className="text-lg font-bold">{summary.total_time_minutes || 0}</p>
              <p className="text-[10px] text-muted-foreground">Minutes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <PoundSterling className="h-4 w-4 mx-auto text-green-600 mb-1" />
              <p className="text-lg font-bold">£{(summary.total_materials_cost || 0).toFixed(0)}</p>
              <p className="text-[10px] text-muted-foreground">Materials</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Camera className="h-4 w-4 mx-auto text-purple-600 mb-1" />
              <p className="text-lg font-bold">{summary.photo_count || 0}</p>
              <p className="text-[10px] text-muted-foreground">Photos</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Log */}
      <Card>
        <CardHeader className="pb-2 p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              Activity Log ({logs.length})
              {queueCount > 0 && (
                <span className="ml-2 text-amber-600 font-normal">+ {queue.filter(q => q.body?.enquiry_id === selectedJob.id).length} queued</span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {/* Queued items */}
          {queue.filter(q => q.body?.enquiry_id === selectedJob.id).length > 0 && (
            <div className="space-y-2 mb-3">
              {queue.filter(q => q.body?.enquiry_id === selectedJob.id).map(q => (
                <div key={q.id} className="flex items-start gap-3 p-2 rounded-lg bg-amber-50 border border-amber-200 border-dashed text-sm" data-testid={`queued-item-${q.id}`}>
                  <div className="mt-0.5">
                    <CloudOff className="h-4 w-4 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-amber-800">{q.label || q.body?.content || "Queued action"}</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">
                      Pending sync • {new Date(q.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {logs.length === 0 && queue.filter(q => q.body?.enquiry_id === selectedJob.id).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No entries yet. Start logging!</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map(log => (
                <div key={log.id} className="flex items-start gap-3 p-2 rounded-lg bg-muted/30 text-sm">
                  <div className="mt-0.5">
                    {log.log_type === 'photo' && <Camera className="h-4 w-4 text-purple-600" />}
                    {log.log_type === 'materials' && <Package className="h-4 w-4 text-amber-600" />}
                    {log.log_type === 'note' && <StickyNote className="h-4 w-4 text-blue-600" />}
                    {log.log_type === 'time' && <Clock className="h-4 w-4 text-green-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{log.content}</p>
                    {log.log_type === 'materials' && log.cost && (
                      <p className="text-xs text-muted-foreground">
                        {log.quantity && `Qty: ${log.quantity} • `}£{log.cost}
                      </p>
                    )}
                    {log.log_type === 'photo' && log.photo_url && (
                      <img 
                        src={getImageUrl(log.photo_url)} 
                        alt={log.content}
                        className="mt-1 rounded-md max-h-24 object-cover"
                      />
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {log.admin_name} • {new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice Button */}
      <Button
        onClick={openInvoicePreview}
        disabled={isGenerating || (!summary?.total_time_minutes && !summary?.total_materials_cost)}
        className="w-full h-12 bg-primary gap-2"
        data-testid="site-generate-invoice"
      >
        {isGenerating ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
        Generate Invoice from Site Logs
      </Button>

      {/* Editable Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Customer Name</label>
              <Input 
                value={invoiceCustomerName} 
                onChange={e => setInvoiceCustomerName(e.target.value)}
                data-testid="invoice-customer-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Customer Address</label>
              <Textarea 
                value={invoiceCustomerAddress} 
                onChange={e => setInvoiceCustomerAddress(e.target.value)}
                rows={2}
                data-testid="invoice-customer-address"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Line Items</label>
                <Button size="sm" variant="outline" onClick={addLineItem} className="h-7 text-xs gap-1">
                  <Plus className="h-3 w-3" /> Add Line
                </Button>
              </div>
              
              {invoiceLineItems.map((item, idx) => (
                <Card key={item.id ?? idx} className="p-3">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Input 
                        value={item.description}
                        onChange={e => updateLineItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        className="text-sm"
                        data-testid={`invoice-line-desc-${idx}`}
                      />
                      <Button 
                        size="sm" variant="ghost" className="h-9 w-9 p-0 shrink-0 text-red-500 hover:text-red-700"
                        onClick={() => removeLineItem(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-muted-foreground">Qty</label>
                        <Input 
                          type="number" step="0.01"
                          value={item.quantity}
                          onChange={e => updateLineItem(idx, 'quantity', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`invoice-line-qty-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Unit Cost (£)</label>
                        <Input 
                          type="number" step="0.01"
                          value={item.unit_cost}
                          onChange={e => updateLineItem(idx, 'unit_cost', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`invoice-line-cost-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Total</label>
                        <div className="h-8 flex items-center text-sm font-medium">
                          £{((item.quantity || 0) * (item.unit_cost || 0)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Grand Total */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="font-semibold">Total</span>
              <span className="text-xl font-bold" data-testid="invoice-grand-total">
                £{invoiceLineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_cost || 0), 0).toFixed(2)}
              </span>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowInvoiceDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={generateInvoice} 
                disabled={isGenerating || invoiceLineItems.length === 0}
                className="flex-1 gap-2"
                data-testid="invoice-confirm-btn"
              >
                {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Create Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
